import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState, useRef } from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {user} from '../../../store/reducers/user';
import { setSnackbar } from '../../../store/reducers/globals';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleDialog from '../../../components/modal/agyleDialog';
import {useTranslation} from 'react-i18next';
import Formatter from '../../../util/formatter';
import moment from 'moment';
import Grid from '@mui/material/Grid';
import Notes from '../../../util/notes';
import {FormControl, MenuItem, Select} from '@mui/material';
import InformationBox from '../../../components/other/informationBox';
import {
    Glass10Icon,
    Glass11Icon,
    Glass12Icon,
    Glass1Icon,
    Glass2Icon,
    Glass3Icon,
    Glass4Icon,
    Glass5Icon,
    Glass6Icon,
    Glass7Icon,
    Glass8Icon,
    Glass9Icon,
} from '../../../components/icons/icons';
import * as _set from 'lodash/set';
import _ from 'lodash';
import {useTheme} from '@mui/material/styles';
import {formAuditIsValid} from '../forms/formsUtil';
import uuidv4 from 'uuid/v4';

export const AUDIT_C_FIELD = 'studies.auditC';

export const getAuditCScore = (auditC, includeExtra = false) => {
    const values = [
        ...Object.values(auditC?.base || {}),
        ...(includeExtra ? Object.values(auditC?.extra || {}) : []),
    ];

    return values.reduce((score, value) => {
        if (value === '' || value === null || value === undefined) return score;
        const numericValue = typeof value === 'object' && value.hasOwnProperty('value') ? Number(value.value) : Number(value);
        return Number.isNaN(numericValue) ? score : score + numericValue;
    }, 0);
};

const normalizeAuditCEntry = (auditC) => {
    if (!auditC || _.isEmpty(auditC)) return auditC;
    const normalized = normalizeAuditCFormData(auditC);

    return {
        ...normalized,
        id:
            normalized.id ||
            normalized.__id ||
            normalized.referenceId ||
            `audit-c-${normalized.date || normalized.when || normalized.who || normalized.by || 'legacy'}`,
        who: normalized.who || normalized.by,
        baseScore: normalized.baseScore ?? getAuditCScore(normalized),
        totalScore: normalized.totalScore ?? getAuditCScore(normalized, true),
    };
};

export const getAuditCEntries = (patient) => {
    const auditC = patient?.studies?.auditC;

    if (_.isArray(auditC?.list)) {
        return auditC.list.map(normalizeAuditCEntry);
    }
    if (auditC && !_.isEmpty(auditC)) {
        return [normalizeAuditCEntry(auditC)];
    }
    return [];
};

export const getLatestAuditCEntry = (patient) => {
    const entries = _.filter(getAuditCEntries(patient), (entry) => !entry?.canceled);
    return _.isEmpty(entries) ? null : entries[0];
};

export const buildAuditCEntry = (auditC, userObject, userFullName, parentForm) => {
    const id = uuidv4() + '';
    const date = moment().toISOString();
    const normalizedAuditC = normalizeAuditCFormData(auditC);
    const baseScore = getAuditCScore(normalizedAuditC);
    const totalScore = getAuditCScore(normalizedAuditC, true);

    return {
        ...normalizedAuditC,
        id,
        who: userFullName,
        by: userFullName,
        login: userObject?.userName,
        date,
        baseScore,
        totalScore,
        parentForm,
    };
};

export const buildAuditCEntryModification = (auditC, userObject, userFullName, parentForm) => {
    const entry = buildAuditCEntry(auditC, userObject, userFullName, parentForm);

    return {
        name: AUDIT_C_FIELD,
        value: entry,
        noteToAddWithRefId: Notes.getAuditCNote(entry),
        referenceId: entry.id,
    };
};

export const buildAuditCListModification = (auditC, patient, userObject, userFullName, parentForm) => {
    const entryModification = buildAuditCEntryModification(auditC, userObject, userFullName, parentForm);
    const list = _.cloneDeep(getAuditCEntries(patient));
    list.unshift(entryModification.value);

    return {
        name: AUDIT_C_FIELD,
        value: {
            list,
            by: userFullName,
            login: userObject?.userName,
            date: moment().toISOString(),
        },
        noteToAddWithRefId: [
            {
                noteToAddWithRefId: entryModification.noteToAddWithRefId,
                referenceId: entryModification.referenceId,
            },
        ],
        idToTrack: entryModification.referenceId,
    };
};

function normalizeAuditCFormData(auditC) {
    const cloneForm = _.cloneDeep(auditC);

    if (!cloneForm.base) {
        cloneForm.base = {
            howOftenDrinkAlcohol: cloneForm.howOftenDrinkAlcohol || '',
            standardDrinkDay: cloneForm.standardDrinkDay || '',
            howOftenMoreDrink: cloneForm.howOftenMoreDrink || '',
        };
    }
    if (!cloneForm.extra) {
        cloneForm.extra = {
            notAbleToStopDrinking: cloneForm.notAbleToStopDrinking || '',
            failedBecauseDrinking: cloneForm.failedBecauseDrinking || '',
            neededAlcoholicMorning: cloneForm.neededAlcoholicMorning || '',
            unableRememberWhatHappened: cloneForm.unableRememberWhatHappened || '',
            injuredResultDrinking: cloneForm.injuredResultDrinking || '',
            relativeConcerned: cloneForm.relativeConcerned || '',
        };
    }

    return cloneForm;
}

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background + ` !important`,
    },
    drinkLabel: {
        maxWidth: 100,
        fontSize: 14,
        textAlign: 'center',
        fontWeight: 400,
    },
    drinkIcon: {
        fontSize: 110,
    },
    outcome: {
        border: '1px solid #7199E6',
        background: '#cfdffd',
        color: '#3F73D6',
        display: 'flex',
        padding: 10,
        justifyContent: 'center',
        gap: 15,

    },
    auditImg: {
        width: '100%', maxWidth: 700,
    },
    scores: {
        fontSize: '15px',
        textAlign: 'right',
        fontWeight: 'bold',
        background: '#4EAC6F',
        width: 'max-content',
        padding: '10px',
        borderRadius: '10px',
        color: 'black',
    },
    outcomeLabel: {
        fontWeight: 'bold',
        fontSize: 15,
        color: '#3F73D6',
    },
    groupRoot: {
        marginBottom: 10,
        padding: 5,
        borderRadius: 5,
    },
    paper: {
        padding: 10,
        backgroundColor: '#252525',
    },
    centerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    userLog: {
        fontSize: 11,
        fontStyle: 'italic',
        color: theme.palette.mode === 'light' ? '#4B4B4B' : '#818181',
    },
    comment: {
        fontSize: 15,
        padding: 5,
        backgroundColor: theme.palette.background.default,
        borderRadius: 5,
    },
    clinician: {
        marginBottom: 10,
    },
    row: {
        width: '100%',
        marginBottom: 10,
    },
    divider: {
        margin: '10px 0',
    },
    label: {
        color: theme.palette.inPatient.questionText,
        fontSize: 12,
        fontWeight: 600,
        marginLeft: 5,
        marginBottom: 5,
    },
    dropDownIcon: {
        borderRadius: 10,
        backgroundColor: theme.palette.inPatient.inputBackground,
    },
    switchLabel: {
        fontSize: 14,
        fontWeight: 600,
        paddingLeft: 10,
    },
    iconDrink: {
        stoke: theme.palette.text,
    },
}));

export function AuditCForm(props) {
    const classes = useStyles();
    const {t} = useTranslation();

    const userObject = useSelector(user);
    const dispatch = useDispatch();
    const tester = Formatter.formatUserFullName(userObject);

    let questions = {
        howOftenDrinkAlcohol: {
            text: 'How Often Do You Have a Drink Containing Alcohol?',
            tab: 'A',
        },
        standardDrinkDay: {
            text: 'How Many Standard Drinks Containing Alcohol Do You Have on a Typical Day?',
            tab: 'B',
        },
        howOftenMoreDrink: {
            text: 'How Often Do You Have 6 or More Drinks on One Occasion?',
            tab: 'C',
        },

        notAbleToStopDrinking: {
            text: 'How often during the last year have you found that you were not able to stop drinking once you had started?',
            tab: 'C',
        },
        failedBecauseDrinking: {
            text: 'How often during the last year have you failed to do what was normally expected from you because of your drinking?',
            tab: 'C',
        },
        neededAlcoholicMorning: {
            text: 'How often during the last year have you needed an alcoholic drink in the morning to get yourself going after a heavy drinking session?',
            tab: 'C',
        },
        unableRememberWhatHappened: {
            text: 'How often during the last year have you been unable to remember what happened the night before because you had been drinking?',
            tab: 'C',
        },
        injuredResultDrinking: {
            text: 'Have you or somebody else been injured as a result of your drinking?',
            tab: 'D',
        },
        relativeConcerned: {
            text: 'Has a relative or friend, doctor or other health worker been concerned about your drinking or suggested that you cut down?',
            tab: 'D',
        },
    };

    let smokingFormInit = {
        base: {
            howOftenDrinkAlcohol: '',
            standardDrinkDay: '',
            howOftenMoreDrink: '',
        },
        extra: {
            notAbleToStopDrinking: '',
            failedBecauseDrinking: '',
            neededAlcoholicMorning: '',
            unableRememberWhatHappened: '',
            injuredResultDrinking: '',
            relativeConcerned: '',
        },

        who: '',
        date: '',
    };

    let tabs = {
        B: [
            {
                text: '0',
                value: 0,
            },
            {
                text: '1 or 2',
                value: 1,

            },
            {
                text: '3 or 4',
                value: 2,
            },
            {
                text: '5 or 6',
                value: 3,
            },
            {
                text: '7 to 9',
                value: 4,
            },
            {
                text: '10 or more',
                value: 5,
            },
        ],
        A: [
            {
                text: 'never',
                value: 0,
            },
            {
                text: 'monthly or less',
                value: 1,
            },
            {
                text: '2-4 times a month',
                value: 2,
            },
            {
                text: '2-3 times a week',
                value: 3,
            },
            {
                text: '4 or more times a week',
                value: 4,
            },
        ],
        C: [
            {
                text: 'never',
                value: 0,
            },
            {
                text: 'less than monthly',
                value: 1,
            },
            {
                text: 'monthly',
                value: 2,
            },
            {
                text: 'weekly',
                value: 3,
            },
            {
                text: 'daily or almost daily',
                value: 4,
            },
        ],
        D: [
            {
                text: 'no',
                value: 0,
            },
            {
                text: 'yes, but not in the last year',
                value: 1,
            },
            {
                text: 'yes, during last year',
                value: 2,
            },
        ],
    };

    useEffect(() => {
        if (props.selectedHistoryForm) return;
        setForm((formLast) => {
            return {
                ...formLast,
                who: tester,
                date: formLast.date || moment().toISOString(),
            };
        });
    }, [userObject, props.selectedHistoryForm]);

    const [form, setForm] = useState(smokingFormInit);

    useEffect(() => {
        const initialAudit =
            props.selectedHistoryForm ||
            props.auditC ||
            getLatestAuditCEntry(props.patient) ||
            getLatestAuditCEntry(props.currentPatientInWard) ||
            null;
        if (initialAudit) {
            let cloneForm = normalizeAuditCFormData(initialAudit);
            // Normalize any stored option objects to primitive values (value) to avoid render loops
            if (cloneForm.base) {
                Object.keys(cloneForm.base).forEach((k) => {
                    const v = cloneForm.base[k];
                    if (v && typeof v === 'object' && v.hasOwnProperty('value')) cloneForm.base[k] = v.value;
                });
            }
            if (cloneForm.extra) {
                Object.keys(cloneForm.extra).forEach((k) => {
                    const v = cloneForm.extra[k];
                    if (v && typeof v === 'object' && v.hasOwnProperty('value')) cloneForm.extra[k] = v.value;
                });
            }
            setForm({
                ...cloneForm,
                who: cloneForm.who || cloneForm.by || tester,
                date: cloneForm.date || moment().toISOString(),
            });
        }
    }, [props.selectedHistoryForm, props.auditC, props.patient?.studies?.auditC, props.currentPatientInWard?.studies?.auditC]);

    // Register submit function for full-form mode so the FullFormContainer can trigger save
    const formRef = useRef(form);
    useEffect(() => {
        formRef.current = form;
    }, [form]);

    const onChangeRef = useRef(props.onChange);
    useEffect(() => {
        onChangeRef.current = props.onChange;
    }, [props.onChange]);

    useEffect(() => {
        if (props.onSubmitFunction) {
            const submitFullForm = () => {
                // Use ref to get latest form without recreating this function on every form change
                const latestForm = formRef.current;
                const onChangeFunc = onChangeRef.current;
                // Validate before allowing final submit
                const valid = formAuditIsValid(latestForm);
                if (!valid) {
                    // Keep form open in full-form mode (return false) so caller won't close
                    // Show snackbar and block submit
                    dispatch(setSnackbar({ type: 'error', message: 'Please complete all required Audit C fields before saving.' }));
                    return false;
                }
                if (onChangeFunc) onChangeFunc([buildAuditCEntryModification(latestForm, userObject, tester, props.parentForm)]);
                return true;
            };
            props.onSubmitFunction(submitFullForm);
        }
    }, [props.onSubmitFunction, props.parentForm, tester, userObject]);

    useEffect(() => {
        if (props.changeFormData) {
            props.changeFormData(form);
        }
        if (props.formIsValid) {
            props.formIsValid(formAuditIsValid(form));
        }
    }, [form]);

    useEffect(() => {
        // When base score is low, clear extra fields — run in effect to avoid state updates during render
        const score = getAuditCScore(form);

        if (score <= 4) {
            const data = { ...form };
            let someChange = false;
            Object.keys(data.extra).forEach((key) => {
                if (data.extra[key]) {
                    data.extra[key] = '';
                    someChange = true;
                }
            });

            if (someChange) setForm(data);
        }
    }, [form]);

    const getMenuItem = (tab) => {
        let items = [];

        items.push(
            <MenuItem key={'notselect'} value={''}>
                <em>Not selected</em>
            </MenuItem>,
        );
        tab.forEach((item, index) => {
            items.push(
                <MenuItem key={index} value={tab[index].value}>
                    {' '}
                    {tab[index].text}{' '}
                </MenuItem>,
            );
        });

        return items;
    };

    const onPropChange = (prop) => {
        return (event, value) => {
            const data = {...form};
            _set(data, prop, event.target.value);

            if (props.formIsChanged) props.formIsChanged(true);
            setForm(data);
        };
    };

    const getDrinkQuestions = (type) => {
        let items = [];

        Object.keys(form[type]).forEach((field, index) => {
            items.push(
                <Grid item xs={12} key={index + 'text'}>
                    <div className={classes.label}> {questions[field].text}</div>
                    <FormControl className={classes.selectorItemFormControl} fullWidth>
                        <Select
                            className={classes.dropDownIcon}
                            labelId="label-howOftenMoreDrink"
                            id={field}
                            variant="outlined"
                            name={type + '.' + field}
                            renderValue={() => {
                                const selected = form[type][field];
                                if (selected === '' || selected === null || selected === undefined) return <em>Not selected</em>;
                                const lookup = tabs[questions[field].tab].find((i) => i.value === selected);
                                return lookup ? lookup.text : selected;
                            }}
                            value={form[type][field]}
                            error={form[type][field] === ''}
                            onChange={onPropChange(type + '.' + field)}
                        >
                            {getMenuItem(tabs[questions[field].tab])}
                        </Select>
                    </FormControl>
                </Grid>,
            );
        });

        return items;
    };

    const getScoreDrink = () => {
        return getAuditCScore(form);
    };

    const getScoreTotalDrink = () => {
        return getAuditCScore(form, true);
    };

    const removeAuditCFields = () => {
        const data = {...form};
        let someChange = false;
        Object.keys(data.extra).forEach((key) => {
            if (data.extra[key]) {
                data.extra[key] = '';
                someChange = true;
            }
        });

        _set(data, 'extra', data.extra);
        if (someChange) setForm(data);
    };

    const theme = useTheme();
    return (
        <Grid container spacing={2} alignItems={'center'}>
            <Grid item xs={12}>
                <InformationBox type="info" label={'Information'}>
                    {t('Audit C is scored on a scale of 0-12 with scores of 0 reflecting no alcohol use. In men, a score of 4 or more is considered positive and in women, a score of 3 or more is considered positive. Generally, the higher the Audit C score, the more likely it is that the patient\'s drinking is affecting his/her health and safety.')}
                </InformationBox>
            </Grid>

            <Grid item xs={12}>
                <InformationBox type="info" label={'The Following Drinks Are One Unit Of Alcohol'}>
                    <div style={{display: 'flex', justifyContent: 'space-around', alignItems: 'baseline'}}>
                        <div style={{textAlign: 'center'}}>
                            <Glass1Icon style={{fontSize: 110, stroke: theme.palette.inPatient.text}}/>
                            <div className={classes.drinkLabel}> {t('Half pint of regular beer, lager or cider')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass2Icon style={{fontSize: 100, stroke: theme.palette.inPatient.text}}/>
                            <div className={classes.drinkLabel}> {t('1 small glass of wine')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass3Icon style={{fontSize: 80, stroke: theme.palette.inPatient.text}}/>
                            <div className={classes.drinkLabel}> {t('1 single measure of spirits')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass4Icon style={{fontSize: 100, stroke: theme.palette.inPatient.text}}/>
                            <div className={classes.drinkLabel}> {t('1 small glass of sherry')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass5Icon style={{fontSize: 100, stroke: theme.palette.inPatient.text}}/>
                            <div className={classes.drinkLabel}> {t('1 single measure of aperitifs')} </div>
                        </div>
                    </div>
                </InformationBox>
            </Grid>

            <Grid item xs={12}>
                <InformationBox type="info" label={'Each of these drinks is MORE than one unit'}>
                    <div style={{display: 'flex', justifyContent: 'space-around', alignItems: 'baseline'}}>
                        <div style={{textAlign: 'center'}}>
                            <Glass6Icon style={{fontSize: 80}}/>
                            <div className={classes.drinkLabel}> {t('Pint of regular beer, lager or cider')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass7Icon style={{fontSize: 80}}/>
                            <div className={classes.drinkLabel}> {t('Pint of Premium Beer/Lager/Cider')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass8Icon style={{fontSize: 110}}/>
                            <div className={classes.drinkLabel}> {t('Alcohol or can/bottle of Regular Lager')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass9Icon style={{fontSize: 80}}/>
                            <div className={classes.drinkLabel}> {t('Can of Super Strength Lager')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass10Icon style={{fontSize: 80}}/>
                            <div className={classes.drinkLabel}> {t('Half pint of regular beer, lager or cider')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass11Icon style={{fontSize: 70}}/>
                            <div className={classes.drinkLabel}> {t('Glass of Wine (175ml)')} </div>
                        </div>
                        <div style={{textAlign: 'center'}}>
                            <Glass12Icon style={{fontSize: 110}}/>
                            <div className={classes.drinkLabel}> {t('Bottle of Wine')} </div>
                        </div>
                    </div>
                </InformationBox>
            </Grid>

            {getDrinkQuestions('base')}

            <Grid item xs={12}>
                <div className={classes.scores}>
                    {t('SCORE OF')}: {getScoreDrink()}
                </div>
            </Grid>

            {getScoreDrink() > 4 &&
                getDrinkQuestions('extra')
            }

            {getScoreDrink() > 4 &&
                <Grid item xs={12}>
                    <div className={classes.scores}>
                        TOTAL SCORE OF: {getScoreTotalDrink()}
                    </div>
                </Grid>
            }
        </Grid>
    );
}

function AuditCFormModal(props) {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const [formData, setFormData] = useState(null);

    const handleSubmit = () => {
        const payload = formData || props.auditC || null;
        // Block sending if required fields not selected
        const valid = formAuditIsValid(payload);
        if (!valid) {
            // show snackbar and keep modal open
            dispatch(setSnackbar({ type: 'error', message: 'Please complete all required Audit C fields before saving.' }));
            return;
        }

        if (props.onChange) props.onChange([buildAuditCListModification(payload, props.patient, userObject, tester)]);

        if (props.onClose) props.onClose();
    };

    const removeOxygen = () => {
        if (props.onChange) {
            props.onChange(
                [
                    {
                        name: AUDIT_C_FIELD,
                        value: null,
                        noteToAdd: Notes.getAuditCNote(null),
                    },
                ],
            );
        }

        if (props.onClose) props.onClose();
    };

    return (
        <AgyleDialog
            open={props.open}
            onClose={props.onClose}
            title={t('Audit C')}
            disableSubmit={props.disableSave}
            onSave={handleSubmit}
            fullWidth
            width="md"
            closeButtonOnHeader
            buttons={[
                <AgyleButton
                    disabled={props.disableSave}
                    onClick={removeOxygen}
                >
                    {'Delete'}
                </AgyleButton>,
            ]}
            contentStyle={classes.root}
            titleStyle={classes.root}
        >
            <AuditCForm
                auditC={getLatestAuditCEntry(props.patient)}
                changeFormData={setFormData}
            />
        </AgyleDialog>
    );
}

export default AuditCFormModal;
